/**
 * The operational unit layer, browser side.
 *
 * `_shared/countPresentation.mjs` and `_shared/orderRecalc.mjs` are the
 * canonical implementations. They depend on the Recipe Lab conversion table,
 * which deliberately has no second browser copy — see the header of
 * `countUnitModel.js` for why.
 *
 * What is mirrored here is everything that needs NO conversion table:
 *
 *   - which vendor product is the default count format, and why
 *   - the arithmetic from a count to a package quantity
 *
 * Both are pure selection and multiplication over rungs the server already
 * built, so they can run on every keystroke, offline, without a round trip.
 * That is the point: the bug this layer exists to fix is an order quantity that
 * did not move when the count did, and a fix that needs a network call is a fix
 * that will be skipped on a slow connection in a walk-in.
 *
 * `countPresentation.test.js` imports the edge modules alongside this one and
 * asserts both pick the same option and produce the same numbers for the same
 * worked examples.
 */

import {
  finiteNonNegative,
  totalFromEntries,
  resolveRowPar,
  computeNeed,
  packagesForNeed,
  resolveMoqPackages,
  MOQ_SOURCES,
  PAR_STATUS,
  MISSING,
  formatQuantity,
  formatPackages,
} from './countUnitModel.js';
import { canonicalPackWord, isContainerWord } from '../../../supabase/functions/_shared/packText.mjs';

const round6 = (n) => Math.round(n * 1e6) / 1e6;
const text = (v) => (v == null ? '' : String(v).trim());
const positive = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Mirrors countPresentation.PURCHASE_EVENT_SOURCE_TYPES. */
export const PURCHASE_EVENT_SOURCE_TYPES = Object.freeze([
  'invoice',
  'receipt',
  'shopping_trip',
  'pos',
]);

/** Mirrors countPresentation.PRESENTATION_SOURCES. */
export const PRESENTATION_SOURCES = Object.freeze({
  PINNED: 'pinned',
  LATEST_PURCHASE: 'latest_verified_purchase',
  LATEST_PACKAGE: 'latest_operational_package',
  LAST_COUNTED: 'last_counted_here',
  AUTO: 'auto_lowest_price',
  PREFERRED: 'preferred_option',
  ONLY: 'only_option',
  CANONICAL: 'canonical',
});

export const PRESENTATION_SOURCE_TEXT = Object.freeze({
  pinned: 'You chose this package.',
  latest_verified_purchase: 'Latest package received on an invoice',
  latest_operational_package: 'Latest package added for this item',
  last_counted_here: 'Last used for this location',
  auto_lowest_price: 'The automatic lowest-price vendor product',
  preferred_option: 'The preferred vendor product',
  only_option: 'The only vendor product for this item',
  canonical: 'No confirmed package count options are available.',
});

/** Mirrors packageIdentity.OPERATIONAL_SOURCE_TEXT. */
export const OPERATIONAL_SOURCE_TEXT = Object.freeze({
  invoice_import: 'Latest package added from an invoice',
  item_import: 'Latest package added from an import',
  item_edit: 'Latest package added by hand',
  count_item_creation: 'Latest package added while counting',
  manual_item_creation: 'Latest package added by hand',
  vendor_item_link: 'Latest package linked from a vendor product',
});

const operationalInstant = (option) => {
  const ms = Date.parse(option?.last_operational_source_at || '');
  return Number.isFinite(ms) ? ms : null;
};

/**
 * The most recently added or materially changed package. No expiry: a package
 * bought months ago and never superseded is still the newest evidence there is.
 */
export function latestOperationalPackage(options) {
  const dated = (options || []).filter((o) => o && operationalInstant(o) !== null);
  if (dated.length === 0) return null;
  return dated.slice().sort((a, b) => {
    const byTime = operationalInstant(b) - operationalInstant(a);
    if (byTime !== 0) return byTime;
    return String(b.id ?? '').localeCompare(String(a.id ?? ''));
  })[0];
}

const LEVELS = Object.freeze({ PACKAGE: 'package', INNER_PACK: 'inner_pack', CANONICAL: 'canonical' });

/** Mirrors countPresentation.selectableRungCount: every rung except the item's
 *  own canonical unit, which is the count field's fallback meaning rather than
 *  a package choice — a 5 gal tub with nothing below it is one way to count,
 *  not two. */
const selectableRungCount = (rungs) =>
  (rungs || []).filter((r) => r.level !== LEVELS.CANONICAL).length;

/* ── Fallback ladder ──────────────────────────────────────────────────────── */

// Units that count discrete things — no half units. Heuristic stand-in for the
// server's unit-kind check, which the browser deliberately does not have.
const COUNTISH_UNITS = new Set([
  'each', 'ea', 'unit', 'units', 'count', 'pc', 'pcs', 'piece', 'pieces',
]);
const isCountishUnit = (u) => COUNTISH_UNITS.has(text(u).toLowerCase());

/**
 * May a PART of this package be counted?
 *
 * Half a 5 gal tub is a real quantity; half a case of 24 bottles is not —
 * that is 12 bottles, which is the rung below. And half an "each" is nothing.
 * Cut-to-order vendors say so on the option itself and always qualify.
 *
 * Exported because the group's operational anchor (utils/groupCountAnchor)
 * re-measures a sibling member's package in the group's own unit and must
 * decide divisibility by exactly this rule — a second copy is how "0.1 BIB"
 * silently floors to 0.
 */
export function packageAllowsFractionalCount({ option, countUnit } = {}) {
  if (option?.allows_fractional_packages === true) return true;
  const packageWord = canonicalPackWord(option?.purchase_unit);
  const countWord = canonicalPackWord(countUnit);
  if (packageWord && countWord && packageWord === countWord && isContainerWord(packageWord)) {
    return true;
  }
  const innerCount = positive(option?.inner_pack_count);
  const hasInner = innerCount !== null && innerCount > 1;
  return !hasInner && !isCountishUnit(countUnit);
}

/**
 * A count ladder built from the option rows alone, for when the purchase-units
 * edge function is unreachable or not deployed.
 *
 * This is NOT a second conversion table. `qty_per_package` is already stored
 * in the item's count unit, and the inner-pack rung is a division of it, so
 * everything here is selection and arithmetic over numbers the server already
 * wrote — the same rule that lets the rest of this module run offline.
 *
 * The server ladder is still better where available (it can parse pack text
 * the columns never cached, and knows real unit kinds); this exists so that a
 * failed edge call degrades to "count in the packages we know about" instead
 * of silently hiding the entire package feature.
 *
 * @returns {{ countUnit, rungs:Array, unresolvedOptions:Array, optionCount:number, source:string }}
 */
export function fallbackCountLadder({ countUnit, options } = {}) {
  const unit = text(countUnit) || null;
  const rungs = [];
  const unresolvedOptions = [];

  if (unit) {
    rungs.push({
      key: 'canonical',
      level: LEVELS.CANONICAL,
      optionId: null,
      entryKind: 'canonical_measure',
      label: unit,
      unitLabel: unit,
      detail: null,
      qtyInCountUnit: 1,
      allowsFractional: !isCountishUnit(unit),
    });
  }

  const active = (options || []).filter((o) => o && text(o.status) === 'active');
  active.forEach((option) => {
    const optionId = option.id ?? null;
    const vendorLabel = text(option.vendor_name_text) || null;
    const qty = positive(option.qty_per_package);
    if (qty === null) {
      unresolvedOptions.push({
        optionId,
        vendorLabel,
        packDescription: text(option.pack_description) || null,
        reason: 'The package size has not been confirmed.',
      });
      return;
    }

    const orderUnit = text(option.purchase_unit) || 'package';
    const innerCount = positive(option.inner_pack_count);
    const hasInner = innerCount !== null && innerCount > 1;
    rungs.push({
      key: `${optionId}:package`,
      level: LEVELS.PACKAGE,
      optionId,
      entryKind: 'full_package',
      label: vendorLabel ? `${vendorLabel} — ${orderUnit}` : orderUnit,
      unitLabel: orderUnit,
      detail: text(option.pack_description) || null,
      qtyInCountUnit: qty,
      allowsFractional: packageAllowsFractionalCount({ option, countUnit: unit }),
    });

    if (hasInner) {
      const perInner = round6(qty / innerCount);
      if (perInner > 0) {
        const noun = text(option.inner_pack_unit) || 'unit';
        const size = positive(option.inner_pack_size);
        rungs.push({
          key: `${optionId}:inner`,
          level: LEVELS.INNER_PACK,
          optionId,
          entryKind: 'loose_inner_pack',
          label: vendorLabel ? `${vendorLabel} — ${noun}` : noun,
          unitLabel: noun,
          detail: size !== null
            ? `${size} ${text(option.inner_pack_size_unit)}`.trim()
            : null,
          qtyInCountUnit: perInner,
          allowsFractional: false,
        });
      }
    }
  });

  return {
    countUnit: unit,
    rungs,
    unresolvedOptions,
    optionCount: active.length,
    source: 'client_fallback',
  };
}

/**
 * The typed number under an active package rung, converted to the count unit.
 * Returns null — never 0 — when the input is not a usable quantity.
 */
export function normalizedFromTyped(typed, rung) {
  // Number(null) and Number('') are both 0 — a missing quantity has to be
  // rejected before coercion or it arrives as a legitimate count of zero.
  const qty = finiteNonNegative(typed);
  const per = Number(rung?.qtyInCountUnit);
  if (qty === null) return null;
  if (!Number.isFinite(per) || per <= 0) return null;
  return round6(qty * per);
}

/**
 * A stored normalized quantity read back in packages, for seeding the input
 * when a counted line is revisited. round6, not display rounding — this value
 * goes back INTO the input, and truncating it would change the stored count
 * on the next keystroke.
 */
export function packageQuantityFromNormalized(normalized, rung) {
  const total = finiteNonNegative(normalized);
  const per = Number(rung?.qtyInCountUnit);
  if (total === null) return null;
  if (!Number.isFinite(per) || per <= 0) return null;
  return round6(total / per);
}

export function isCompletedPurchase(option) {
  return PURCHASE_EVENT_SOURCE_TYPES.includes(text(option?.source_type));
}

/**
 * A completed purchase somebody confirmed. Every clause excludes something a
 * user would be surprised to see change their count format: an order-guide
 * price, a hand-typed correction, an invoice still awaiting approval, an AI
 * match nobody has looked at.
 */
export function isVerifiedCompletedPurchase(option) {
  if (!option) return false;
  if (text(option.status) !== 'active') return false;
  if (!isCompletedPurchase(option)) return false;
  if (text(option.verification_status) !== 'verified') return false;
  if (option.is_approved === false) return false;
  return true;
}

const purchaseInstant = (option) => {
  const ms = Date.parse(option?.effective_date || '');
  return Number.isFinite(ms) ? ms : null;
};
const recordedInstant = (option) => {
  const ms = Date.parse(option?.updated_at || option?.created_at || '');
  return Number.isFinite(ms) ? ms : 0;
};

/** The most recent verified completed purchase, or null. */
export function latestVerifiedPurchase(options) {
  const eligible = (options || []).filter(
    (o) => isVerifiedCompletedPurchase(o) && purchaseInstant(o) !== null,
  );
  if (eligible.length === 0) return null;
  return eligible.slice().sort((a, b) => {
    const byDate = purchaseInstant(b) - purchaseInstant(a);
    if (byDate !== 0) return byDate;
    const byRecorded = recordedInstant(b) - recordedInstant(a);
    if (byRecorded !== 0) return byRecorded;
    return String(b.id ?? '').localeCompare(String(a.id ?? ''));
  })[0];
}

const rungsForOption = (rungs, optionId) =>
  (rungs || []).filter((r) => r.optionId != null && String(r.optionId) === String(optionId));

function pickRung(optionRungs, preferredUnit) {
  if (optionRungs.length === 0) return null;
  const wanted = text(preferredUnit).toLowerCase();
  if (wanted) {
    const match = optionRungs.find((r) => text(r.unitLabel).toLowerCase() === wanted);
    if (match) return match;
  }
  return optionRungs.find((r) => r.level === LEVELS.PACKAGE) || optionRungs[0];
}

/**
 * The count format for one row, from rungs the server already built.
 *
 * Precedence — identical to the edge module, and the reason the toggle means
 * anything:
 *
 *   1. a pin, when follow-latest is off
 *   2. the most recent verified purchase, when follow-latest is on
 *   3. a pin, when follow-latest is on but nothing has been bought yet
 *   4. preferred → 5. only → 6. the item's own unit
 *
 * @param {object} args
 * @param {object} args.row      count_items / count_template_items row
 * @param {Array}  args.options  purchase_options rows
 * @param {Array}  args.rungs    ladder from the purchase-units edge function
 * @param {Array}  [args.unresolved] options whose package size is unconfirmed
 */
export function resolveCountPresentation({ row, options, rungs, unresolved } = {}) {
  const warnings = [];
  const active = (options || []).filter((o) => o && text(o.status) === 'active');
  const followLatest = row?.follow_latest_purchase !== false;
  const pinnedId = row?.count_display_purchase_option_id ?? null;
  const preferredUnit = row?.count_display_unit ?? null;

  const countable = active.filter((o) =>
    (rungs || []).some((r) => r.optionId != null && String(r.optionId) === String(o.id)),
  );

  const reasonFor = (source, option) =>
    source === PRESENTATION_SOURCES.LATEST_PACKAGE
      ? OPERATIONAL_SOURCE_TEXT[text(option?.last_operational_source_type)] ||
        PRESENTATION_SOURCE_TEXT[source]
      : PRESENTATION_SOURCE_TEXT[source] || null;

  const build = (option, rung, source) => ({
    optionId: option?.id ?? null,
    option: option ?? null,
    // Keyed on usable count RUNGS, never vendors and not packages alone: one
    // vendor selling a 4 lb bag and a 2 × 5 lb case is two packages and needs
    // a selector, and ONE package with a real inner rung (case AND loose
    // bottle) is two ways to count the same shelf and needs one too. Four
    // vendors of whom one has a confirmed package with nothing below it is one
    // way to count and must not get one. (Parity: countPresentation.mjs.)
    needsSelector: selectableRungCount(rungs) >= 2,
    countableCount: countable.length,
    defaultReason: reasonFor(source, option),
    unitLabel: rung?.unitLabel ?? null,
    qtyInCountUnit: rung?.qtyInCountUnit ?? null,
    source,
    sourceText: PRESENTATION_SOURCE_TEXT[source] || null,
    rung: rung ?? null,
    rungs: rungs || [],
    options: active,
    unresolved: unresolved || [],
    followLatest,
    pinnedOptionId: pinnedId ?? null,
    isPinned: pinnedId != null && option != null && String(pinnedId) === String(option.id),
    label: describeCountFormat({ rung, option }),
    warnings,
  });

  const attempt = (option, source, unitHint) => {
    if (!option) return null;
    const rung = pickRung(rungsForOption(rungs, option.id), unitHint);
    return rung ? build(option, rung, source) : null;
  };

  const pinnedOption = pinnedId == null ? null : active.find((o) => String(o.id) === String(pinnedId));
  if (pinnedId != null && !pinnedOption) {
    warnings.push('The pinned count format is no longer an active vendor product.');
  }

  if (!followLatest) {
    const pinned = attempt(pinnedOption, PRESENTATION_SOURCES.PINNED, preferredUnit);
    if (pinned) return pinned;
    if (pinnedOption) {
      warnings.push('The pinned count format has no confirmed package size, so it cannot be counted in.');
    }
  }

  if (followLatest) {
    // 2. Delivered on an accepted invoice — direct evidence it arrived.
    const latest = latestVerifiedPurchase(active);
    const sameAsPinned = latest && pinnedId != null && String(latest.id) === String(pinnedId);
    const followed = attempt(
      latest,
      PRESENTATION_SOURCES.LATEST_PURCHASE,
      sameAsPinned ? preferredUnit : null,
    );
    if (followed) return followed;

    // 3. Most recently added or materially changed. Somebody typed it rather
    //    than received it, so it ranks below a delivery — but it is still the
    //    newest thing anyone has said about what this item comes in.
    const newest = attempt(latestOperationalPackage(active), PRESENTATION_SOURCES.LATEST_PACKAGE, null);
    if (newest) return newest;

    // 4. What this location was counted in last time.
    const lastUsedId = row?.count_last_used_purchase_option_id ?? null;
    const lastUsed = attempt(
      lastUsedId == null ? null : active.find((o) => String(o.id) === String(lastUsedId)),
      PRESENTATION_SOURCES.LAST_COUNTED,
      null,
    );
    if (lastUsed) return lastUsed;

    const pinned = attempt(pinnedOption, PRESENTATION_SOURCES.PINNED, preferredUnit);
    if (pinned) return pinned;
  }

  // Deliberately NO price rung here (parity with countPresentation.mjs). The
  // count format is a claim about what is on the shelf; which vendor is
  // currently cheapest says nothing about that, and letting the Savings
  // Review's automatic winner lead would change the counting package under
  // the user every time the market moved.
  const preferred = attempt(active.find((o) => o.is_preferred === true), PRESENTATION_SOURCES.PREFERRED, null);
  if (preferred) return preferred;

  // "The only option" means the only COUNTABLE one — an item whose other
  // vendors have unsized packages has one package, and should use it rather
  // than dropping to the canonical unit as though nothing were known.
  if (countable.length === 1) {
    const only = attempt(countable[0], PRESENTATION_SOURCES.ONLY, null);
    if (only) return only;
  }

  const canonical = (rungs || []).find((r) => r.level === LEVELS.CANONICAL) || null;
  return build(null, canonical, PRESENTATION_SOURCES.CANONICAL);
}

/** "Sysco — jar (12 floz)" / "gal". */
export function describeCountFormat({ rung, option } = {}) {
  if (!rung) return null;
  if (rung.level === LEVELS.CANONICAL) return rung.unitLabel;
  const parts = [rung.label || rung.unitLabel];
  if (rung.detail) parts.push(`(${rung.detail})`);
  else if (option?.pack_description) parts.push(`(${text(option.pack_description)})`);
  return parts.filter(Boolean).join(' ');
}

/**
 * "cases" from "case", "boxes" from "box" — the ladder names its rungs in the
 * singular, and a noun already ending in "s" needs nothing.
 *
 * The sibilant rule is not decoration. `box`, `bunch`, `batch`, `pouch` and
 * `dish` are ordinary count units in a produce kitchen, and a bare +"s"
 * renders "Need 2 boxs to reach PAR" on the primary PAR/COUNT/NEED tiles.
 * Exported because the group anchor's equivalence sentence and the Shopping
 * Review both say the same nouns out loud, and three copies of one rule is
 * how two of them stay wrong.
 */
export const pluralNoun = (noun, qty = 2) => {
  const unit = text(noun) || 'unit';
  if (qty === 1) return unit;
  // A noun already ending in "s" is left alone — "pcs" and "lbs" are
  // abbreviations that are already plural, and "pcses" is worse than the bug
  // this rule exists to fix. Only the sibilants that genuinely take "es" are
  // added to the old behaviour.
  if (/s$/i.test(unit)) return unit;
  return /(x|z|ch|sh)$/i.test(unit) ? `${unit}es` : `${unit}s`;
};

/**
 * A stored NORMALIZED quantity read in the format a line is counted in.
 *
 * This is the one place a count-card number turns from the stored physical
 * quantity into the words a counter reads. PAR, COUNT and NEED all go through
 * it, so they can never disagree about which package they are speaking in —
 * the "PAR 1 BIB / COUNT 0.1 BIB / NEED 4.5 gal" card, where two tiles had
 * been converted and the third had only been relabelled, is the failure this
 * function exists to make impossible.
 *
 * The stored quantity is untouched. Only its EXPRESSION changes: 4.5 gal in a
 * 5 gal BIB format is 0.9 BIB, and 4.5 gal in no format at all is 4.5 gal.
 *
 * @param {number|null} normalized  the quantity in the item's count unit
 * @param {object|null} format      a rung / package view ({ qtyInCountUnit, unitLabel })
 * @param {string} countUnit        the item's own count unit
 * @returns {{ value:number|null, unit:string|null, text:string,
 *             inFormat:boolean, countUnit:string|null, normalized:number|null }}
 */
export function expressInCountFormat(normalized, format, countUnit, { maxDecimals = 3 } = {}) {
  const per = Number(format?.qtyInCountUnit);
  const usable = !!format && Number.isFinite(per) && per > 0;
  const stored = finiteNonNegative(normalized);
  const unit = usable
    ? (text(format.unitLabel) || text(countUnit) || null)
    : (text(countUnit) || null);
  const qty = usable ? packageQuantityFromNormalized(stored, format) : stored;
  if (qty === null) {
    return { value: null, unit, text: MISSING, inFormat: usable, countUnit: text(countUnit) || null, normalized: stored };
  }
  const rounded = Math.round(qty * 10 ** maxDecimals) / 10 ** maxDecimals;
  // A package noun pluralizes only above one — "0.9 BIB" and "0.5 bottle" are
  // parts of one package, and "0.9 BIBs" reads as a quantity of packages
  // nobody has. Measure units (gal, lb) never pluralize; pluralNoun leaves
  // them alone because the ladder names them in their own singular form.
  const label = usable ? pluralNoun(unit, rounded > 1 ? 2 : 1) : unit;
  return {
    value: rounded,
    unit,
    text: label ? `${rounded} ${label}` : String(rounded),
    inFormat: usable,
    countUnit: text(countUnit) || null,
    normalized: stored,
  };
}

/**
 * "1 case = 12 bottles" — the sentence that lets somebody check the conversion
 * against the case in their hands instead of trusting it.
 *
 * For a package with a discrete inner rung the DISCRETE relationship leads: a
 * counter holding one full case and two loose bottles can verify "12 bottles"
 * against the label on the case; "1 case = 144 floz" asks them to trust a
 * measure they cannot see. The inner count is read back out of the two rungs
 * the ladder already built (the inner rung is derived from the package total,
 * so the ratio recovers it exactly) — nothing is re-interpreted here.
 *
 * A package with no inner rung (a tub of bulk mayonnaise) keeps the measured
 * form, as does an inner rung itself ("1 bottle = 12 floz"). The canonical
 * rung has no equivalence to state.
 */
export function describeRungEquivalence(rung, countUnit, rungs) {
  if (!rung || rung.qtyInCountUnit == null) return null;
  if (rung.level === LEVELS.CANONICAL) return null;
  if (rung.level === LEVELS.PACKAGE) {
    const inner = (rungs || []).find(
      (r) => r.level === LEVELS.INNER_PACK
        && r.optionId != null
        && String(r.optionId) === String(rung.optionId),
    );
    const perInner = Number(inner?.qtyInCountUnit);
    if (inner && Number.isFinite(perInner) && perInner > 0) {
      const count = Number(rung.qtyInCountUnit) / perInner;
      const whole = Math.round(count);
      // The inner rung is only built from a whole count, so a non-integer
      // ratio means something is off — fall back to the measured form rather
      // than print "1 case = 11.98 bottles".
      if (whole > 1 && Math.abs(count - whole) < 1e-6) {
        return `1 ${rung.unitLabel} = ${whole} ${pluralNoun(inner.unitLabel, whole)}`;
      }
    }
  }
  return `1 ${rung.unitLabel} = ${formatQuantity(rung.qtyInCountUnit, countUnit)}`;
}

/**
 * "Count cases + loose bottles" — the mixed-package count action named in the
 * shelf's own words, for the option a line is currently counted in. Null when
 * that option has no discrete rung below it (a tub, a sack): the generic
 * "Count several packages" wording stands, because there is no inner unit to
 * name.
 */
export function describeMixedCountAction(rungs, currentRung) {
  if (!currentRung || currentRung.optionId == null) return null;
  const siblings = (rungs || []).filter(
    (r) => r.optionId != null && String(r.optionId) === String(currentRung.optionId),
  );
  const packageRung = siblings.find((r) => r.level === LEVELS.PACKAGE);
  const innerRung = siblings.find((r) => r.level === LEVELS.INNER_PACK);
  if (!packageRung || !innerRung) return null;
  return `Count ${pluralNoun(packageRung.unitLabel)} + loose ${pluralNoun(innerRung.unitLabel)}`;
}

/**
 * The "Count as" menu. Unresolved packages appear disabled with a reason rather
 * than being hidden — a user who bought from that vendor needs to see why they
 * cannot count in it, and where to go to fix it.
 */
export function countFormatChoices(presentation, { countUnit } = {}) {
  if (!presentation) return [];
  const currentKey = presentation.rung?.key ?? null;
  const optionById = new Map((presentation.options || []).map((o) => [String(o.id), o]));
  const unit = text(countUnit) || null;
  const defaultOptionId = presentation.optionId == null ? null : String(presentation.optionId);

  const choices = (presentation.rungs || []).map((rung) => {
    const option = rung.optionId == null ? null : optionById.get(String(rung.optionId)) || null;
    const isDefault = defaultOptionId !== null && String(rung.optionId ?? '') === defaultOptionId;
    return {
      key: rung.key,
      optionId: rung.optionId,
      unitLabel: rung.unitLabel,
      level: rung.level,
      label: rung.label,
      detail: rung.detail,
      // A vendor name alone does not identify what is in your hand: Costco may
      // sell this as a 4 lb bag AND as a case of 2 × 5 lb bags.
      vendorLabel: text(option?.vendor_name_text) || null,
      vendorSku: text(option?.vendor_sku) || null,
      productDescription: text(option?.vendor_description) || null,
      packDescription: text(option?.pack_description) || rung.detail || null,
      contents: rung.qtyInCountUnit != null && unit ? `${round6(rung.qtyInCountUnit)} ${unit}` : null,
      packagePrice: positive(option?.package_price),
      qtyInCountUnit: rung.qtyInCountUnit,
      allowsFractional: rung.allowsFractional,
      entryKind: rung.entryKind,
      selected: rung.key === currentKey,
      isDefaultPackage: isDefault,
      defaultReason: isDefault ? presentation.defaultReason : null,
      disabled: false,
      reason: null,
    };
  });
  (presentation.unresolved || []).forEach((u) => {
    choices.push({
      key: `${u.optionId}:unresolved`,
      optionId: u.optionId,
      unitLabel: null,
      level: null,
      label: u.vendorLabel ? `${u.vendorLabel} — needs unit review` : 'Needs unit review',
      detail: u.packDescription,
      qtyInCountUnit: null,
      allowsFractional: false,
      entryKind: null,
      selected: false,
      disabled: true,
      reason: u.reason,
    });
  });
  return choices;
}

/**
 * What to persist when a user picks a format. Choosing one IS pinning it, so
 * follow-latest goes off in the same write — otherwise the next verified
 * invoice would silently undo the choice they just made.
 */
export function countFormatSelection(choice, { countUnit } = {}) {
  if (!choice || choice.disabled) return null;
  return {
    count_display_purchase_option_id: choice.optionId ?? null,
    count_display_unit: choice.unitLabel ?? null,
    follow_latest_purchase: false,
    count_display_conversion_snapshot: {
      purchase_option_id: choice.optionId ?? null,
      unit_label: choice.unitLabel ?? null,
      level: choice.level ?? null,
      qty_in_count_unit: choice.qtyInCountUnit ?? null,
      count_unit: countUnit ?? null,
      source: PRESENTATION_SOURCES.PINNED,
    },
  };
}

export const SWITCH_ACTIONS = Object.freeze({
  CHANGE_IN_PLACE: 'change_in_place',
  OFFER_CHOICE: 'offer_choice',
});

/**
 * What to do when the counter picks a different package with a count already
 * entered.
 *
 * The number in the box counts a SPECIFIC package. "2" meaning two Costco 4 lb
 * bags is 8 lb; carrying that 2 across to a 10 lb case silently makes it 20 lb.
 * Nobody typed 20, nobody would notice, and the order that follows is wrong by
 * more than a case. So a nonzero entry is never reinterpreted on its own, and
 * adding a second entry is the default offer — mixed old-and-new packaging is
 * the normal state of a shelf mid-transition.
 */
export function planPackageSwitch({ currentQuantity, currentChoice, nextChoice } = {}) {
  const qty = Number(currentQuantity);
  const hasCount = Number.isFinite(qty) && qty > 0;
  const nextLabel = text(nextChoice?.vendorLabel) || text(nextChoice?.label) || 'the new package';

  if (!hasCount) {
    return {
      action: SWITCH_ACTIONS.CHANGE_IN_PLACE,
      canChangeInPlace: true,
      addLabel: null,
      changeLabel: null,
      warning: null,
    };
  }

  const currentLabel = text(currentChoice?.vendorLabel) || text(currentChoice?.label) || 'this package';
  const currentUnit = text(currentChoice?.unitLabel) || 'package';
  const nextUnit = text(nextChoice?.unitLabel) || 'package';

  return {
    action: SWITCH_ACTIONS.OFFER_CHOICE,
    canChangeInPlace: true,
    addLabel: `Add ${nextLabel} as another count entry`,
    changeLabel: `Change this entry to ${nextLabel}`,
    warning:
      `${qty} ${currentUnit}${qty === 1 ? '' : 's'} of ${currentLabel} would be re-read as ` +
      `${qty} ${nextUnit}${qty === 1 ? '' : 's'} of ${nextLabel}, which is a different quantity.`,
  };
}

/**
 * Clears a stored normalization so an edited entry is re-resolved.
 *
 * `resolveEntry` trusts a stored `normalized_quantity` — it has to, because a
 * measure typed in another unit can only be converted by the server. That trust
 * becomes a bug the moment the quantity is edited and the old normalized value
 * survives: "2 gal" corrected to "3 gal" would keep counting as 2. Every editor
 * change to a quantity, unit, or package must go through here.
 */
export function touchEntry(entry, changes) {
  return {
    ...entry,
    ...changes,
    normalized_quantity: null,
    normalized_unit: null,
    conversion_status: null,
    conversion_reason: null,
  };
}

/* ── Count → order, in one pass ───────────────────────────────────────────── */

export const ORDER_STATUS = Object.freeze({
  READY: 'ready',
  NO_OPTION: 'no_option',
  UNRESOLVED_PACKAGE: 'unresolved_package',
  UNKNOWN_NEED: 'unknown_need',
  SATISFIED: 'satisfied',
});

export const ORDER_STATUS_TEXT = Object.freeze({
  no_option: 'Choose a vendor product to see how many packages to order.',
  unresolved_package:
    'This vendor package has no confirmed size, so the number of packages is a minimum, not a calculation.',
  unknown_need: 'The shortage is unknown, so no order quantity can be calculated.',
  satisfied: 'On hand meets the PAR.',
});

/**
 * On-hand for one count line, preferring its raw entries.
 *
 * A row with no entries was counted the old way; its stored number is not
 * missing, and `is_counted` is the only thing separating "counted as none" from
 * "not reached yet".
 */
export function rowOnHand({ row, entries, rungs } = {}) {
  const list = entries || [];
  if (list.length > 0) {
    const result = totalFromEntries(list, rungs);
    return {
      total: result.total,
      source: 'entries',
      hasUnresolved: result.hasUnresolved,
      needsServer: result.needsServer,
      warnings: result.warnings,
    };
  }
  // actual_quantity first: countEntryService writes it, and the server reads
  // it first. The mirror preferring current_count meant the two sides could
  // disagree about legacy on-hand whenever the columns diverged.
  const stored = finiteNonNegative(row?.actual_quantity ?? row?.current_count);
  if (row?.is_counted && stored !== null) {
    return { total: stored, source: 'stored', hasUnresolved: false, needsServer: false, warnings: [] };
  }
  return { total: null, source: 'uncounted', hasUnresolved: false, needsServer: false, warnings: [] };
}

/**
 * Every number from the shelf to the purchase order, recomputed from scratch.
 *
 * Nothing stored is read as an input except a manual override, so there is no
 * intermediate value that can go stale between a count and its order.
 *
 * @param {object} args
 * @param {Array}  args.rows            every count row for this item, all locations
 * @param {Object} [args.entriesByRow]  { [rowId]: entries[] }
 * @param {Array}  [args.options]
 * @param {Array}  [args.rungs]         ladder from the edge function
 * @param {string} args.countUnit
 * @param {*}      [args.selectedOptionId]  the ORDER option, not the count format
 */
export function recomputeItemOrder({
  rows,
  entriesByRow,
  options,
  rungs,
  countUnit,
  selectedOptionId,
  legacyMoq,
  manualPackages,
} = {}) {
  const rowList = (rows || []).filter(Boolean);
  const optionList = (options || []).filter(Boolean);
  const warnings = [];

  const locations = rowList.map((row) => {
    const entries = (entriesByRow || {})[String(row.id)] || [];
    const result = rowOnHand({ row, entries, rungs });
    warnings.push(...result.warnings);
    return {
      rowId: row.id ?? null,
      location: row.location ?? row.item_snapshot?.location ?? null,
      total: result.total,
      source: result.source,
      hasUnresolved: result.hasUnresolved,
      entryCount: entries.length,
    };
  });

  const contributing = locations.filter((l) => l.source !== 'uncounted');
  const resolvedTotals = contributing.filter((l) => l.total != null);
  const onHand =
    resolvedTotals.length > 0
      ? round6(resolvedTotals.reduce((sum, l) => sum + Number(l.total), 0))
      : null;
  const uncountedLocations = locations.length - contributing.length;

  // PAR is item-global. Rows should agree; legacy templates sometimes do not,
  // so the largest resolvable value wins — the rule the app has always used.
  //
  // A legacy PAR typed in a unit other than the count unit ("10 gal" against a
  // fl oz item) cannot be normalized here: that needs the conversion table.
  // It comes back unresolved with parNeedsServer set, so the caller asks
  // purchase-units rather than the UI quietly showing no PAR at all — which is
  // precisely the disappearing-PAR complaint this layer exists to fix.
  let par = null;
  let parResolution = null;
  let parNeedsServer = false;
  rowList.forEach((row) => {
    const resolution = resolveRowPar(row, countUnit);
    if (resolution.status === PAR_STATUS.VALID && resolution.normalized != null) {
      if (par == null || resolution.normalized > par) {
        par = resolution.normalized;
        parResolution = resolution;
      }
    } else if (resolution.status === PAR_STATUS.UNRESOLVED) {
      // A stated PAR we could not convert is a question for the server, not an
      // item without a PAR.
      if (resolution.display?.value != null) parNeedsServer = true;
      if (!parResolution) parResolution = resolution;
    }
  });

  // The related-group fold (utils/countGroupFold) hides sibling members'
  // rows and stamps their contribution onto the favored member's first row.
  // The baseline joins on-hand and the floor joins the PAR max exactly as in
  // orderRecalc.mjs, applySharedOrderMathBulk and buildNeedsFromCountItems —
  // the five need implementations must agree. A null baseline means no
  // hidden row was counted; that is not a zero.
  let groupBaseline = null;
  let groupParFloor = null;
  rowList.forEach((row) => {
    const baseline = finiteNonNegative(row?.group_baseline_count);
    if (baseline !== null) groupBaseline = (groupBaseline ?? 0) + baseline;
    const floor = positive(row?.group_par_floor);
    if (floor !== null && (groupParFloor === null || floor > groupParFloor)) groupParFloor = floor;
    // A hidden sibling stated a PAR the normalized resolution refused ("2
    // cases", no package). The group's PAR needs the server/package answer —
    // same surfacing as an unresolvable PAR on a visible row.
    if (row?.group_par_unresolved) parNeedsServer = true;
  });
  const foldedOnHand = onHand == null
    ? (groupBaseline == null ? null : round6(groupBaseline))
    : round6(onHand + (groupBaseline ?? 0));
  if (groupParFloor !== null && (par == null || groupParFloor > par)) par = groupParFloor;

  const { need, reason: needReason } = computeNeed({ par, onHand: foldedOnHand });

  const selectedOption =
    selectedOptionId == null
      ? null
      : optionList.find((o) => String(o.id) === String(selectedOptionId)) || null;

  const order = orderForOption({ need, option: selectedOption, options: optionList, rungs, legacyMoq });

  const manual = finiteNonNegative(manualPackages);
  const packages = manual !== null ? manual : order.packages;
  const perPackage = order.qtyPerPackage;
  const quantityReceived = packages != null && perPackage != null ? round6(packages * perPackage) : null;
  const overage = quantityReceived != null && need != null ? round6(quantityReceived - need) : null;
  const packagePrice = positive(selectedOption?.package_price);
  const extendedCost = packages != null && packagePrice != null ? round6(packages * packagePrice) : null;

  return {
    countUnit: countUnit ?? null,
    locations,
    onHand: foldedOnHand,
    onHandPartial: uncountedLocations > 0 || resolvedTotals.length !== contributing.length,
    uncountedLocations,
    countedLocations: contributing.length,
    hasUnresolvedEntries: locations.some((l) => l.hasUnresolved),

    par,
    parUnit: parResolution?.unit ?? countUnit ?? null,
    parStatus: parResolution?.status ?? PAR_STATUS.EMPTY,
    parDisplay: parResolution?.display ?? null,
    parNeedsServer,

    need,
    needReason,

    selectedOptionId: selectedOption?.id ?? null,
    orderUnit: order.orderUnit,
    qtyPerPackage: perPackage,
    moq: order.moq,
    packages,
    calculatedPackages: order.packages,
    isManualOverride: manual !== null,
    quantityReceived,
    overage,
    packagePrice,
    extendedCost,
    orderIncrement: order.orderIncrement ?? null,

    // Parity with orderRecalc.mjs: a manual package override answers "how
    // many?" even when the need is unknown, so the status upgrades to READY
    // exactly as the server's does.
    status: manual !== null && order.status === ORDER_STATUS.UNKNOWN_NEED
      ? ORDER_STATUS.READY
      : order.status,
    statusText: ORDER_STATUS_TEXT[order.status] || null,
    warnings: [...warnings, ...order.warnings],
  };
}

/** Packages for a need against one vendor product, from its package rung. */
export function orderForOption({ need, option, options, rungs, legacyMoq } = {}) {
  const warnings = [];
  const moqResolution = resolveMoqPackages({ selectedOption: option, options: options || [], legacyMoq });

  if (!option) {
    return {
      packages: null,
      qtyPerPackage: null,
      orderUnit: null,
      moq: moqResolution.packages,
      status: need == null ? ORDER_STATUS.UNKNOWN_NEED : ORDER_STATUS.NO_OPTION,
      warnings,
    };
  }

  const packageRung = rungsForOption(rungs, option.id).find((r) => r.level === LEVELS.PACKAGE) || null;
  const qtyPerPackage = positive(packageRung?.qtyInCountUnit);
  const orderUnit = packageRung?.unitLabel || text(option.purchase_unit) || 'package';
  const allowsFractional = option.allows_fractional_packages === true;
  const orderIncrement = positive(option.order_increment);

  // A resolved MOQ of 1 from the DEFAULT source is "nobody said", not "the
  // vendor requires one" — forwarding it would clamp a cut-to-order 0.7 lb up
  // to a whole pound.
  const statedMoq = moqResolution.source === MOQ_SOURCES.DEFAULT ? null : moqResolution.packages;
  const result = packagesForNeed({ need, qtyPerPackage, moqPackages: statedMoq, allowsFractional, orderIncrement });

  let status = ORDER_STATUS.READY;
  if (need == null) status = ORDER_STATUS.UNKNOWN_NEED;
  else if (result.reason === 'unresolved_package') status = ORDER_STATUS.UNRESOLVED_PACKAGE;
  else if (need <= 0) status = ORDER_STATUS.SATISFIED;

  if (qtyPerPackage === null) {
    warnings.push(
      `The size of one ${orderUnit} is not confirmed, so ${result.packages ?? 'the minimum'} is a minimum rather than a calculated quantity.`,
    );
  }

  return {
    packages: result.packages,
    qtyPerPackage,
    quantityReceived: result.quantityReceived,
    overage: result.overage,
    orderUnit,
    moq: moqResolution.packages,
    allowsFractional,
    orderIncrement,
    status,
    warnings,
  };
}

/**
 * Whether a saved draft still matches the counts. Compared on the inputs a
 * person would call "the count changed", not only on the resulting package
 * count — rounding can absorb a real change, and a reviewer would then approve
 * a line whose stated on-hand is wrong.
 */
export function allocationIsStale(saved, computed) {
  if (!saved || !computed) return { stale: false, changed: [] };
  if (saved.status === 'sent' || saved.status === 'received') return { stale: false, changed: [] };

  const changed = [];
  const differs = (a, b) => {
    const x = a == null ? null : Number(a);
    const y = b == null ? null : Number(b);
    if (x === null && y === null) return false;
    if (x === null || y === null) return true;
    return Math.abs(x - y) > 1e-6;
  };

  if (differs(saved.on_hand_quantity ?? saved.onHand, computed.onHand)) changed.push('on hand');
  if (differs(saved.par_quantity ?? saved.par, computed.par)) changed.push('PAR');
  if (differs(saved.needed_quantity ?? saved.need, computed.need)) changed.push('need');
  if (differs(saved.packages ?? saved.package_quantity, computed.packages)) changed.push('packages');
  const savedOption = saved.purchase_option_id ?? saved.selectedOptionId ?? null;
  if (String(savedOption ?? '') !== String(computed.selectedOptionId ?? '')) changed.push('vendor');

  return { stale: changed.length > 0, changed };
}

/**
 * The order line in words, every number carrying its unit. "Order 4" is the
 * display this replaces — four cases, pounds, or ounces are three different
 * deliveries.
 */
export function describeOrderLine(computed, { itemName, vendorLabel } = {}) {
  if (!computed) return null;
  const unit = computed.countUnit || '';
  return {
    itemName: text(itemName) || null,
    vendorLabel: text(vendorLabel) || null,
    onHand: formatQuantity(computed.onHand, unit),
    par: formatQuantity(computed.par, unit),
    need: formatQuantity(computed.need, unit),
    vendorPackage:
      computed.qtyPerPackage != null
        ? `${round6(computed.qtyPerPackage)} ${unit} per ${computed.orderUnit || 'package'}`
        : null,
    order: formatPackages(computed.packages, computed.orderUnit),
    quantityReceived: formatQuantity(computed.quantityReceived, unit),
    overage:
      computed.overage == null ? null : `${formatQuantity(Math.abs(computed.overage), unit)} over`,
    packagePrice:
      computed.packagePrice == null
        ? null
        : `$${Number(computed.packagePrice).toFixed(2)} per ${computed.orderUnit || 'package'}`,
    extendedCost: computed.extendedCost == null ? null : `$${Number(computed.extendedCost).toFixed(2)}`,
    statusText: computed.statusText,
  };
}

export { MISSING };
